import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { io } from "socket.io-client";
import styled from "styled-components";
import { MdPlayArrow, MdClose, MdInfoOutline, MdEventSeat } from "react-icons/md";
import Navbar from "../components/Navbar";
import BackBar from "../components/Ticketing/BackBar";
import { apiGetEvent } from "../api/ticketingApi";
import { fmtTime, fmtMoney, minTicketPrice, experienceLabel } from "../components/Ticketing/format";

const safeColor = (value, fallback) => /^#[0-9A-F]{6}$/i.test(value || "") ? value : fallback;
const contrastText = (hex) => {
  const rgb = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const lum = rgb.map((v) => v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4)
    .reduce((sum, v, i) => sum + v * [0.2126, 0.7152, 0.0722][i], 0);
  return (lum + 0.05) / 0.05 >= 1.05 / (lum + 0.05) ? "#0D1A2F" : "#FFFFFF";
};
const safeEmbedUrl = (value) => {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    return url.protocol === "https:" && ["www.youtube-nocookie.com", "player.vimeo.com"].includes(host)
      ? url.toString()
      : "";
  } catch { return ""; }
};
const dayKey = (iso) => {
  const date = new Date(iso);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};
const longDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" }) : "";
const runtime = (minutes) => minutes ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : "";
const presentation = (showing) => experienceLabel(showing);

const EventDetailPage = () => {
  const { eventId } = useParams();
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [showTrailer, setShowTrailer] = useState(false);
  const [showPolicies, setShowPolicies] = useState(false);

  // `silent` skips the full-page spinner and can only ever improve what is on
  // screen: a socket-driven refetch that fails leaves the showtimes the
  // customer is reading in place instead of replacing them with an error.
  const load = useCallback((alive, { silent = false } = {}) => {
    if (!silent) { setLoading(true); setError(""); }
    apiGetEvent(eventId)
      .then((result) => {
        if (!alive.current) return;
        setError("");
        setData(result);
        // Keep the date the customer is looking at. Re-seeding from
        // showings[0] on every reload would yank them back to the first day
        // mid-browse, so only fall back when their day no longer has a showing.
        const days = (result?.showings || []).map((showing) => dayKey(showing.startsAt));
        setSelectedDay((current) => (current && days.includes(current) ? current : days[0] || ""));
        setLoading(false);
      })
      .catch(() => {
        if (!alive.current || silent) return;
        setError("We couldn't load this movie.");
        setLoading(false);
      });
  }, [eventId]);

  useEffect(() => {
    const alive = { current: true };
    load(alive);

    // LIVE showtimes — parity with the events list and both mobile apps. The
    // backend emits `ticketing:update` whenever a showing is created,
    // published, updated, cancelled or deleted. This page never listened, so a
    // customer sitting on it while ops published a showtime saw nothing until
    // they navigated away and back. Filter to THIS event so an unrelated
    // organizer's publish doesn't refetch under them.
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      transports: ["websocket", "polling"],
    });
    socket.on("connect", () => socket.emit("join", "ticketing"));
    socket.on("ticketing:update", (msg) => {
      if (!msg || !["event", "showing"].includes(msg.type)) return;
      // Older server builds emit a deletion with no eventId — refetch rather
      // than risk missing a change to the event on screen.
      if (msg.eventId && String(msg.eventId) !== String(eventId)) return;
      load(alive, { silent: true });
    });

    return () => {
      alive.current = false;
      socket.off("ticketing:update");
      socket.disconnect();
    };
  }, [load, eventId]);

  const event = data?.event;
  const showings = useMemo(() => data?.showings || [], [data]);
  const organizer = data?.organizer;
  const trailerEmbed = safeEmbedUrl(event?.trailerEmbedUrl);
  const primary = safeColor(organizer?.theme?.primaryColor, "#83B524");
  const secondary = safeColor(organizer?.theme?.secondaryColor, "#0D1A2F");
  const onPrimary = contrastText(primary);
  const dates = useMemo(() => {
    const seen = new Set();
    return showings.filter((showing) => {
      const key = dayKey(showing.startsAt);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [showings]);
  const venueGroups = useMemo(() => {
    const groups = new Map();
    showings.filter((showing) => !selectedDay || dayKey(showing.startsAt) === selectedDay).forEach((showing) => {
      const key = String(showing.venue?._id || showing.venueId || "venue");
      if (!groups.has(key)) groups.set(key, { venue: showing.venue, list: [] });
      groups.get(key).list.push(showing);
    });
    return [...groups.values()];
  }, [showings, selectedDay]);
  const policyEntries = Object.entries(organizer?.customerPolicies || {}).filter(([, value]) => value);
  const meta = [
    runtime(event?.durationMinutes), event?.contentRating, event?.language,
    ...(event?.genres || []),
  ].filter(Boolean);

  return (
    <>
      <Navbar />
      <Page style={{ "--cinema-primary": primary, "--cinema-secondary": secondary, "--cinema-on-primary": onPrimary }}>
        <BackBar fallback="/events" />
        {loading && <State>Loading movie…</State>}
        {!loading && error && <State>{error}</State>}
        {!loading && !error && event && (
          <>
            <CinemaHero $banner={event.bannerUrl}>
              <HeroShade />
              <Poster $src={event.posterUrl}>
                {!event.posterUrl && <Fallback>{(event.title || "M").charAt(0)}</Fallback>}
                {trailerEmbed && (
                  <Play type="button" onClick={() => setShowTrailer(true)} aria-label={`Watch ${event.title} trailer`}>
                    <MdPlayArrow />
                  </Play>
                )}
              </Poster>
              <HeroInfo>
                <Eyebrow>{event.status === "COMING_SOON" ? "Coming soon" : event.category === "CINEMA" ? "Now showing" : event.category}</Eyebrow>
                <h1>{event.title}</h1>
                <Meta>{meta.map((item) => <span key={item}>{item}</span>)}</Meta>
                {event.synopsis && <Synopsis>{event.synopsis}</Synopsis>}
                <Credits>
                  {event.director && <div><strong>Director</strong>{event.director}</div>}
                  {event.cast?.length > 0 && <div><strong>Cast</strong>{event.cast.join(", ")}</div>}
                  {event.studio && <div><strong>Studio</strong>{event.studio}</div>}
                </Credits>
                <HeroActions>
                  {trailerEmbed && <PrimaryButton type="button" onClick={() => setShowTrailer(true)}><MdPlayArrow /> Watch trailer</PrimaryButton>}
                  {policyEntries.length > 0 && <GhostButton type="button" onClick={() => setShowPolicies(true)}><MdInfoOutline /> Booking terms</GhostButton>}
                </HeroActions>
                {organizer && <Presenter>{organizer.logoUrl && <img src={organizer.logoUrl} alt="" />}<span>Tickets by <strong>{organizer.name}</strong></span></Presenter>}
              </HeroInfo>
            </CinemaHero>

            {event.status === "COMING_SOON" && showings.length === 0 ? (
              <ComingSoon>
                <div>Opening {longDate(event.releaseDate) || "soon"}</div>
                <p>Showtimes and seat booking will appear here as soon as the cinema opens sales.</p>
              </ComingSoon>
            ) : (
              <BookingSection>
                <SectionHead><div><span>Book tickets</span><h2>Choose a date and cinema</h2></div><MdEventSeat /></SectionHead>
                <DateStrip>
                  {dates.map((showing, index) => {
                    const key = dayKey(showing.startsAt);
                    return <DateButton key={key} $active={key === selectedDay} onClick={() => setSelectedDay(key)}>
                      <small>{index === 0 ? "Next" : new Date(showing.startsAt).toLocaleDateString(undefined, { weekday: "short" })}</small>
                      <strong>{new Date(showing.startsAt).getDate()}</strong>
                      <span>{new Date(showing.startsAt).toLocaleDateString(undefined, { month: "short" })}</span>
                    </DateButton>;
                  })}
                </DateStrip>
                {venueGroups.map(({ venue, list }) => (
                  <VenueCard key={venue?._id || venue?.name || "venue"}>
                    <VenueHead><div><h3>{venue?.name || "Venue"}</h3><span>{venue?.address || ""}</span></div></VenueHead>
                    <ExperienceRows>
                      {Object.entries(list.reduce((groups, showing) => {
                        const label = presentation(showing) || "Admission";
                        (groups[label] = groups[label] || []).push(showing);
                        return groups;
                      }, {})).map(([label, experienceShowings]) => (
                        <Experience key={label}>
                          <ExperienceLabel>{label}</ExperienceLabel>
                          <Showtimes>{experienceShowings.map((showing) => {
                            const price = minTicketPrice(showing);
                            return <Showtime key={showing._id} onClick={() => history.push(`/showing/${showing._id}`)}>
                              <strong>{fmtTime(showing.startsAt)}</strong>
                              {price != null && <span>from {fmtMoney(price)}</span>}
                            </Showtime>;
                          })}</Showtimes>
                        </Experience>
                      ))}
                    </ExperienceRows>
                  </VenueCard>
                ))}
                {showings.length === 0 && <State>No upcoming showtimes.</State>}
              </BookingSection>
            )}
          </>
        )}
      </Page>

      {showTrailer && trailerEmbed && (
        <ModalShade onClick={() => setShowTrailer(false)}>
          <VideoModal onClick={(e) => e.stopPropagation()}>
            <ModalClose onClick={() => setShowTrailer(false)} aria-label="Close trailer"><MdClose /></ModalClose>
            <iframe title={`${event.title} trailer`} src={`${trailerEmbed}?autoplay=1`} allow="autoplay; encrypted-media; picture-in-picture" sandbox="allow-scripts allow-same-origin allow-presentation" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
          </VideoModal>
        </ModalShade>
      )}
      {showPolicies && (
        <DrawerShade onClick={() => setShowPolicies(false)}>
          <PolicyDrawer onClick={(e) => e.stopPropagation()} style={{ "--cinema-primary": primary }}>
            <DrawerHead><div><small>{organizer?.name}</small><h2>Booking terms</h2></div><button onClick={() => setShowPolicies(false)}><MdClose /></button></DrawerHead>
            {policyEntries.map(([key, value]) => <Policy key={key}><h3>{({ ticketPurchase: "Ticket purchase", admission: "Admission", showtime: "Showtime", refunds: "Returns & exchanges", venueRules: "Venue rules", recording: "Recording policy", foodAndDrinks: "Food & drinks" })[key] || key}</h3><p>{value}</p></Policy>)}
          </PolicyDrawer>
        </DrawerShade>
      )}
    </>
  );
};

const Page = styled.main`max-width:1180px;margin:0 auto;padding:calc(var(--lusuku-nav-h,130px) + 12px) 20px 64px;min-height:70vh;@media(max-width:680px){padding-left:14px;padding-right:14px;}`;
const State = styled.div`color:#6b7280;padding:32px 0;text-align:center;`;
const CinemaHero = styled.section`position:relative;display:grid;grid-template-columns:250px 1fr;gap:32px;min-height:390px;padding:34px;border-radius:22px;overflow:hidden;background:${({ $banner }) => $banner ? `url(${$banner}) center/cover` : "var(--cinema-secondary)"};box-shadow:0 18px 48px rgba(13,26,47,.2);@media(max-width:720px){grid-template-columns:122px 1fr;gap:16px;padding:20px;min-height:0;}@media(max-width:480px){grid-template-columns:1fr;}`;
const HeroShade = styled.div`position:absolute;inset:0;background:linear-gradient(90deg,rgba(5,12,24,.96) 0%,rgba(5,12,24,.84) 48%,rgba(5,12,24,.68) 100%);`;
const Poster = styled.div`position:relative;z-index:1;width:100%;aspect-ratio:2/3;border-radius:16px;background:${({ $src }) => $src ? `url(${$src}) center/cover` : "#e5e7eb"};box-shadow:0 14px 30px rgba(0,0,0,.38);display:flex;align-items:center;justify-content:center;@media(max-width:480px){width:150px;}`;
const Fallback = styled.span`font-size:64px;font-weight:900;color:#94a3b8;`;
const Play = styled.button`position:absolute;inset:50% auto auto 50%;transform:translate(-50%,-50%);width:58px;height:58px;border-radius:50%;border:0;background:var(--cinema-primary);color:var(--cinema-on-primary);display:grid;place-items:center;cursor:pointer;box-shadow:0 8px 26px rgba(0,0,0,.35);svg{font-size:34px;}`;
const HeroInfo = styled.div`position:relative;z-index:1;color:#fff;align-self:center;h1{font-size:clamp(30px,4vw,52px);line-height:1.04;margin:7px 0 13px;font-weight:900;}@media(max-width:680px){h1{font-size:26px;}}`;
const Eyebrow = styled.div`font-size:12px;font-weight:900;letter-spacing:.15em;text-transform:uppercase;color:var(--cinema-primary);`;
const Meta = styled.div`display:flex;gap:0;flex-wrap:wrap;color:#d9e0ea;font-size:13px;font-weight:600;span+span:before{content:"•";margin:0 9px;color:#778397;}`;
const Synopsis = styled.p`max-width:720px;color:#e6eaf0;line-height:1.65;font-size:14.5px;margin:18px 0 14px;`;
const Credits = styled.div`display:grid;gap:5px;font-size:12.5px;color:#d7dde6;div{display:grid;grid-template-columns:62px 1fr;gap:7px;}strong{color:#fff;}`;
const HeroActions = styled.div`display:flex;gap:10px;flex-wrap:wrap;margin-top:20px;`;
const PrimaryButton = styled.button`border:0;border-radius:10px;background:var(--cinema-primary);color:var(--cinema-on-primary);padding:11px 17px;font-weight:800;display:flex;align-items:center;gap:6px;cursor:pointer;svg{font-size:21px;}`;
const GhostButton = styled(PrimaryButton)`background:rgba(255,255,255,.11);border:1px solid rgba(255,255,255,.34);color:#fff;`;
const Presenter = styled.div`display:flex;align-items:center;gap:9px;margin-top:17px;color:#cbd5e1;font-size:12.5px;img{width:32px;height:32px;border-radius:8px;object-fit:cover;background:#fff;}strong{color:#fff;}`;
const BookingSection = styled.section`padding:36px 0 0;`;
const SectionHead = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;span{font-size:12px;font-weight:900;letter-spacing:.12em;color:var(--cinema-primary);text-transform:uppercase;}h2{margin:3px 0 0;font-size:25px;color:#182231;}svg{font-size:34px;color:var(--cinema-primary);}`;
const DateStrip = styled.div`display:flex;gap:10px;overflow-x:auto;padding:2px 1px 15px;`;
const DateButton = styled.button`flex:0 0 84px;border-radius:13px;border:1px solid ${({ $active }) => $active ? "var(--cinema-primary)" : "#e1e6ec"};background:${({ $active }) => $active ? "var(--cinema-primary)" : "#fff"};color:${({ $active }) => $active ? "var(--cinema-on-primary)" : "#263244"};padding:10px 8px;cursor:pointer;display:grid;gap:1px;small{font-size:10px;text-transform:uppercase;font-weight:800;}strong{font-size:22px;}span{font-size:11px;}`;
const VenueCard = styled.article`background:#fff;border:1px solid #e7ebf0;border-radius:16px;margin:0 0 14px;overflow:hidden;box-shadow:0 7px 22px rgba(15,23,42,.06);`;
const VenueHead = styled.div`padding:16px 18px;border-left:4px solid var(--cinema-primary);background:#fbfcfd;h3{margin:0;color:#202b3c;font-size:17px;}span{color:#7a8492;font-size:11.5px;}`;
const ExperienceRows = styled.div`padding:7px 18px 15px;`;
const Experience = styled.div`display:grid;grid-template-columns:minmax(150px,220px) 1fr;align-items:center;gap:16px;padding:11px 0;border-bottom:1px solid #edf0f3;&:last-child{border-bottom:0;}@media(max-width:620px){grid-template-columns:1fr;gap:7px;}`;
const ExperienceLabel = styled.div`font-size:12px;font-weight:800;color:#566173;text-transform:uppercase;letter-spacing:.04em;`;
const Showtimes = styled.div`display:flex;gap:9px;flex-wrap:wrap;`;
const Showtime = styled.button`border:1px solid color-mix(in srgb,var(--cinema-primary) 45%,#dfe5eb);background:#fff;color:#263244;border-radius:10px;padding:9px 12px;cursor:pointer;display:grid;gap:2px;text-align:left;&:hover{background:var(--cinema-primary);color:var(--cinema-on-primary);}strong{font-size:14px;}span{font-size:9.5px;opacity:.75;}`;
const ComingSoon = styled.section`margin-top:24px;border-radius:16px;padding:26px;background:color-mix(in srgb,var(--cinema-primary) 10%,#fff);border:1px solid color-mix(in srgb,var(--cinema-primary) 28%,#fff);div{font-size:22px;font-weight:900;color:#1f2937;}p{color:#657080;margin:7px 0 0;}`;
const ModalShade = styled.div`position:fixed;inset:0;z-index:2000;background:rgba(2,7,15,.88);display:grid;place-items:center;padding:24px;`;
const VideoModal = styled.div`position:relative;width:min(960px,94vw);aspect-ratio:16/9;background:#000;border-radius:15px;overflow:hidden;iframe{border:0;width:100%;height:100%;}`;
const ModalClose = styled.button`position:absolute;right:10px;top:10px;z-index:2;width:36px;height:36px;border-radius:50%;border:0;background:rgba(0,0,0,.68);color:#fff;display:grid;place-items:center;cursor:pointer;svg{font-size:23px;}`;
const DrawerShade = styled.div`position:fixed;inset:0;z-index:2100;background:rgba(13,26,47,.52);display:flex;justify-content:flex-end;`;
const PolicyDrawer = styled.aside`width:min(480px,94vw);height:100%;overflow:auto;background:#fff;padding:24px 26px;box-shadow:-14px 0 40px rgba(15,23,42,.2);`;
const DrawerHead = styled.div`display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:18px;border-bottom:1px solid #e6e9ed;small{color:var(--cinema-primary);font-weight:800;}h2{margin:3px 0 0;font-size:24px;}button{border:0;background:#f1f3f5;border-radius:9px;width:36px;height:36px;display:grid;place-items:center;cursor:pointer;}svg{font-size:22px;}`;
const Policy = styled.section`padding:18px 0;border-bottom:1px solid #edf0f3;h3{font-size:15px;margin:0 0 7px;color:#283446;}p{white-space:pre-line;margin:0;color:#5f6a79;font-size:13.5px;line-height:1.65;}`;

export default EventDetailPage;
